import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { STATUSES } from "../lib/constants";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Rocket, UserPlus, Send, MessageSquare, Repeat, Target, Sparkles, Wand2, PlugZap, CheckCircle2,
} from "lucide-react";

const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const fmtWk = (iso) => { const d = new Date(iso); return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }); };

function Kpi({ icon: Icon, label, value, sub, accent = "text-primary", testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className={`h-4 w-4 ${accent}`} />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-3 font-display text-3xl font-extrabold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function HygioCard({ label, value, sub, connected, testid }) {
  return (
    <div className={`rounded-2xl border p-5 ${connected ? "border-border bg-card" : "border-dashed border-border bg-card/40"}`} data-testid={testid}>
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {connected ? (
        <>
          <p className="mt-3 font-display text-3xl font-extrabold tabular-nums">{value ?? "—"}</p>
          <p className="text-xs text-muted-foreground mt-1">{value == null ? "Mapping pending" : sub}</p>
        </>
      ) : (
        <div className="mt-3 flex items-center gap-2 text-muted-foreground">
          <PlugZap className="h-4 w-4 text-amber-400" />
          <span className="text-sm">Connect Hygio to unlock</span>
        </div>
      )}
    </div>
  );
}

const RANGES = [30, 60, 90];

export default function Launch() {
  const [days, setDays] = useState(90);
  const { data } = useQuery({
    queryKey: ["launch", days],
    queryFn: () => api.get("/launch/metrics", { params: { days } }).then((r) => r.data),
  });
  const k = data?.kpis || {};
  const hygio = data?.hygio || { connected: false };
  const supply = hygio.supply;
  const series = (data?.series || []).map((s) => ({ ...s, label: fmtWk(s.week) }));

  return (
    <div data-testid="launch-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Rocket className="h-7 w-7 text-primary" /> Launch Cockpit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Your 90-day go-to-market metrics — steer supply vs demand at a glance.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${hygio.connected ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`} data-testid="hygio-status">
            {hygio.connected ? <CheckCircle2 className="h-3.5 w-3.5" /> : <PlugZap className="h-3.5 w-3.5" />}
            Hygio {hygio.connected ? "connected" : "not connected"}
          </div>
          <div className="flex rounded-lg border border-border overflow-hidden">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setDays(r)} data-testid={`range-${r}`}
                className={`px-3 py-1.5 text-sm transition-colors ${days === r ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground"}`}>
                {r}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi icon={UserPlus} label="New leads" value={k.new_leads ?? "—"} sub={`${data?.days || days}-day window`} testid="kpi-new-leads" />
        <Kpi icon={Send} label="Outreach sent" value={k.outreach ?? "—"} sub={`${k.replies || 0} replies`} accent="text-sky-400" testid="kpi-outreach" />
        <Kpi icon={MessageSquare} label="Reply rate" value={pct(k.reply_rate)} sub="of outreach" accent="text-sky-400" testid="kpi-reply-rate" />
        <Kpi icon={Repeat} label="Pilot → Recurring" value={pct(k.pilot_to_recurring)} sub={`${k.recurring || 0} of ${k.pilots || 0} pilots`} accent="text-emerald-400" testid="kpi-pilot-recurring" />
        <Kpi icon={Target} label="Campaign conv." value={pct(k.campaign_conv_rate)} sub={`${k.campaign_converted || 0}/${k.campaign_enrolled || 0} enrolled`} accent="text-purple-400" testid="kpi-campaign-conv" />
      </div>

      <div className="rounded-2xl border border-border bg-card p-6" data-testid="growth-chart">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-bold">Demand engine — weekly</h2>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={series} margin={{ left: -18, right: 8, top: 4 }}>
            <defs>
              <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22D3EE" stopOpacity={0.5} /><stop offset="100%" stopColor="#22D3EE" stopOpacity={0} /></linearGradient>
              <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.4} /><stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient>
              <linearGradient id="gRep" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="new_leads" name="New leads" stroke="#22D3EE" strokeWidth={2} fill="url(#gLeads)" />
            <Area type="monotone" dataKey="outreach" name="Outreach" stroke="#8B5CF6" strokeWidth={2} fill="url(#gOut)" />
            <Area type="monotone" dataKey="replies" name="Replies" stroke="#10B981" strokeWidth={2} fill="url(#gRep)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="pipeline-funnel">
          <h2 className="font-display text-lg font-bold mb-4">Demand pipeline</h2>
          <div className="space-y-3">
            {STATUSES.map((st) => {
              const val = data?.by_status?.[st.key] || 0;
              const max = Math.max(1, ...STATUSES.map((s) => data?.by_status?.[s.key] || 0));
              return (
                <div key={st.key} data-testid={`funnel-${st.key}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className={st.text}>{st.label}</span>
                    <span className="tabular-nums text-muted-foreground">{val}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(val / max) * 100}%`, background: st.hex }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6" data-testid="supply-panel">
          <div className="flex items-center gap-2 mb-1">
            <Wand2 className="h-4 w-4 text-amber-400" />
            <h2 className="font-display text-lg font-bold">Supply side — Hygio</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {hygio.connected
              ? "Live from Hygio's operations database (spotless_db)."
              : "These light up once Hygio's booking database is connected."}
          </p>
          {hygio.connected && supply ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-xl border border-border bg-card p-4" data-testid="supply-cleaners">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Active cleaners</span>
                  <p className="mt-2 font-display text-2xl font-extrabold tabular-nums">{supply.cleaners.approved}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{supply.cleaners.online} online now</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4" data-testid="supply-areas">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Service areas</span>
                  <p className="mt-2 font-display text-2xl font-extrabold tabular-nums">{supply.service_areas.enabled}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{supply.service_areas.cities.length} cities</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4" data-testid="supply-coverage">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Area coverage</span>
                  <p className="mt-2 font-display text-2xl font-extrabold tabular-nums">{pct(supply.cleaners.coverage_rate)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{supply.cleaners.covered_areas}/{supply.service_areas.enabled} FSAs staffed</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Coverage map</p>
                <div className="flex flex-wrap gap-1.5" data-testid="supply-cities">
                  {supply.service_areas.cities.map((c) => (
                    <span key={c.city} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs">
                      {c.city} <span className="text-muted-foreground">·{c.fsas}</span>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Booking metrics — activate when Hygio takes bookings</p>
                <div className="grid grid-cols-3 gap-4">
                  <HygioCard label="Cleans / wk" value={hygio.cleans_per_week} sub="completed jobs" connected={hygio.connected} testid="hygio-cleans" />
                  <HygioCard label="Fill rate" value={hygio.fill_rate != null ? pct(hygio.fill_rate) : null} sub="bookings staffed" connected={hygio.connected} testid="hygio-fillrate" />
                  <HygioCard label="Utilization" value={hygio.cleaner_utilization != null ? pct(hygio.cleaner_utilization) : null} sub="cleaner capacity" connected={hygio.connected} testid="hygio-utilization" />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <HygioCard label="Cleans / wk" value={hygio.cleans_per_week} sub="completed jobs" connected={hygio.connected} testid="hygio-cleans" />
              <HygioCard label="Fill rate" value={hygio.fill_rate != null ? pct(hygio.fill_rate) : null} sub="bookings staffed" connected={hygio.connected} testid="hygio-fillrate" />
              <HygioCard label="Utilization" value={hygio.cleaner_utilization != null ? pct(hygio.cleaner_utilization) : null} sub="cleaner capacity" connected={hygio.connected} testid="hygio-utilization" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
